import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Select from 'react-select';
import {
  getAllInstructions,
  setInstructions,
  deleteInstructions,
  getAllDealerAssistant,
  applyInstructions,
  setDealerAssistant,
  getTalkliveInstances,
  updateTalkliveInstance,
} from '../services/api';

const ASSISTANT_INSTRUCTION_ID_FIELDS = [
  'instructions_id',
  'facebook_instructions_id',
  'sms_instructions_id',
  'confirmation_instructions_id',
];

const APPLY_FIELD_LABELS = {
  instructions_id: 'Basic (instructions_id)',
  facebook_instructions_id: 'Facebook (facebook_instructions_id)',
  sms_instructions_id: 'SMS (sms_instructions_id)',
  confirmation_instructions_id: 'Confirmation (confirmation_instructions_id)',
};

const DEFAULT_APPLY_TO_FIELDS = {
  instructions_id: true,
  facebook_instructions_id: false,
  sms_instructions_id: false,
  confirmation_instructions_id: false,
};

function formatAssistantLabel(assistant) {
  const id = assistant?.assistant_id || 'unknown';
  const name = assistant?.name?.trim();
  return name ? `${name} (${id})` : id;
}

function applyInstructionFieldsToAssistant(assistant, instructionId, fields) {
  const updated = { ...assistant };
  for (const field of fields) {
    updated[field] = instructionId;
    if (updated.optionalFields) {
      updated.optionalFields = { ...updated.optionalFields, [field]: instructionId };
    }
  }
  return updated;
}

function assistantUsesInstructionId(assistant, instructionId) {
  if (!assistant || !instructionId) return false;

  for (const field of ASSISTANT_INSTRUCTION_ID_FIELDS) {
    if (assistant[field] === instructionId) return true;
    if (assistant.optionalFields?.[field] === instructionId) return true;
  }

  if (Array.isArray(assistant.talklive_instances)) {
    return assistant.talklive_instances.some((inst) => inst?.instructions_id === instructionId);
  }

  return false;
}

function applyVersionToAssistant(assistant, oldId, newId) {
  const updated = { ...assistant };
  let changed = false;

  for (const field of ASSISTANT_INSTRUCTION_ID_FIELDS) {
    if (updated[field] === oldId) {
      updated[field] = newId;
      changed = true;
    }
    if (updated.optionalFields?.[field] === oldId) {
      updated.optionalFields = { ...updated.optionalFields, [field]: newId };
      changed = true;
    }
  }

  if (Array.isArray(updated.talklive_instances)) {
    updated.talklive_instances = updated.talklive_instances.map((inst) => {
      if (inst?.instructions_id === oldId) {
        changed = true;
        return { ...inst, instructions_id: newId };
      }
      return inst;
    });
  }

  return { updated, changed };
}

function assistantNeedsTalkliveInstanceSync(assistant, oldId) {
  return Array.isArray(assistant.talklive_instances) &&
    assistant.talklive_instances.some((inst) => inst?.instructions_id === oldId);
}

async function updateTalkliveInstancesForAssistant(assistantId, oldId, newId) {
  try {
    const res = await getTalkliveInstances(assistantId);
    const data = res.data;
    const instances = Array.isArray(data) ? data : (data?.instances || []);
    const matching = instances.filter((inst) => inst?.instructions_id === oldId);

    if (matching.length === 0) {
      return { updated: 0, failed: 0 };
    }

    const results = await Promise.allSettled(
      matching.map((inst) =>
        updateTalkliveInstance(assistantId, inst.instance_id, {
          ...inst,
          instructions_id: newId,
        })
      )
    );

    const failedInstanceIds = matching
      .filter((_, index) => results[index].status === 'rejected')
      .map((inst) => inst.instance_id);

    return {
      updated: results.filter((r) => r.status === 'fulfilled').length,
      failed: failedInstanceIds.length,
      failedInstanceIds,
    };
  } catch {
    // No TalkLive instances API data for this assistant — not an instruction update failure
    return { updated: 0, failed: 0, failedInstanceIds: [] };
  }
}

async function propagateInstructionVersionToAssistants(oldId, newId) {
  const res = await getAllDealerAssistant();
  const assistants = res.data?.data || [];

  const assistantsToSave = [];
  const talkliveAssistantIds = new Set();

  for (const assistant of assistants) {
    const { updated, changed } = applyVersionToAssistant(assistant, oldId, newId);
    if (changed) {
      assistantsToSave.push(updated);
    }

    if (assistantNeedsTalkliveInstanceSync(assistant, oldId)) {
      talkliveAssistantIds.add(assistant.assistant_id);
    }
  }

  const saveResults = await Promise.allSettled(
    assistantsToSave.map((assistant) => setDealerAssistant(assistant))
  );

  let talkliveInstancesUpdated = 0;
  let talkliveInstancesFailed = 0;
  const talkliveFailedDetails = [];

  const talkliveAssistantIdsList = [...talkliveAssistantIds];
  const assistantById = new Map(assistants.map((assistant) => [assistant.assistant_id, assistant]));

  const talkliveResults = await Promise.allSettled(
    talkliveAssistantIdsList.map(async (assistantId) => {
      const result = await updateTalkliveInstancesForAssistant(assistantId, oldId, newId);
      return { assistantId, ...result };
    })
  );

  for (let index = 0; index < talkliveResults.length; index += 1) {
    const result = talkliveResults[index];
    const assistantId = talkliveAssistantIdsList[index];

    if (result.status === 'fulfilled') {
      talkliveInstancesUpdated += result.value.updated;
      talkliveInstancesFailed += result.value.failed;
      if (result.value.failed > 0) {
        const assistant = assistantById.get(result.value.assistantId);
        const label = formatAssistantLabel(assistant || { assistant_id: result.value.assistantId });
        const instances = result.value.failedInstanceIds.join(', ');
        talkliveFailedDetails.push(`${label}${instances ? ` [instances: ${instances}]` : ''}`);
      }
    } else {
      talkliveInstancesFailed += 1;
      const assistant = assistantById.get(assistantId);
      talkliveFailedDetails.push(formatAssistantLabel(assistant || { assistant_id: assistantId }));
    }
  }

  const assistantsUpdated = saveResults.filter((r) => r.status === 'fulfilled').length;
  const assistantsFailedList = saveResults
    .map((result, index) => ({ result, assistant: assistantsToSave[index] }))
    .filter(({ result }) => result.status === 'rejected')
    .map(({ assistant }) => formatAssistantLabel(assistant));

  return {
    scanned: assistants.length,
    assistantsUpdated,
    assistantsFailed: assistantsFailedList.length,
    assistantsFailedList,
    talkliveInstancesUpdated,
    talkliveInstancesFailed,
    talkliveFailedDetails,
    affected: assistants.filter((a) => assistantUsesInstructionId(a, oldId)).length,
  };
}

// Modal component (pattern from ConversationItem.js)
function Modal({ isOpen, title, children, onClose, contentClassName, contentStyle }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay">
      <div
        className={contentClassName ? `modal-content ${contentClassName}` : 'modal-content'}
        style={contentStyle}
      >
        <h2>{title}</h2>
        {children}
        <button onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

// Helper to get next version id
function getNextVersionId(baseId, sets) {
  const regex = new RegExp(`^${baseId}_v(\\d+)$`);
  const versions = sets
    .map(s => {
      const match = s.id.match(regex);
      return match ? parseInt(match[1], 10) : null;
    })
    .filter(v => v !== null);
  const next = versions.length ? Math.max(...versions) + 1 : 1;
  return `${baseId}_v${next}`;
}

const Instructions = () => {
  const [instructionSets, setInstructionSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [markdown, setMarkdown] = useState('');
  const [showNameModal, setShowNameModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newSetName, setNewSetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [allAssistants, setAllAssistants] = useState([]);
  const [selectedAssistants, setSelectedAssistants] = useState([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyToFields, setApplyToFields] = useState({ ...DEFAULT_APPLY_TO_FIELDS });
  const [successMessage, setSuccessMessage] = useState(null);

  // Fetch all instructions on mount
  useEffect(() => {
    setLoading(true);
    getAllInstructions()
      .then(res => {
        setInstructionSets(res.data || []);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load instructions');
        setLoading(false);
      });
  }, []);

  // On select change, fill markdown
  const handleSelect = (option) => {
    setError(null);
    setSuccessMessage(null);
    const set = instructionSets.find(s => s.id === option.value);
    setSelectedSet(set);
    setMarkdown(set ? set.instructions : '');
  };

  // Create new set
  const handleCreate = () => {
    setError(null);
    setSuccessMessage(null);
    setSelectedSet(null);
    setMarkdown('');
  };

  // Save logic
  const handleSave = () => {
    setError(null);
    if (!selectedSet) {
      setShowNameModal(true);
    } else {
      setShowConfirmModal(true);
    }
  };

  // Confirm save for new set
  const handleNameSubmit = async () => {
    const id = `${newSetName}_v1`;
    const newSet = { id, instructions: markdown };
    setLoading(true);
    setError(null);
    try {
      await setInstructions(id, markdown);
      setInstructionSets([...instructionSets, newSet]);
      setSelectedSet(newSet);
      setShowNameModal(false);
      setNewSetName('');
    } catch (err) {
      setError('Failed to save new instructions');
    }
    setLoading(false);
  };

  // Confirm save for existing set (versioned)
  const handleConfirmSave = async () => {
    const oldId = selectedSet.id;
    const baseId = oldId.replace(/_v\d+$/, '');
    const id = getNextVersionId(baseId, instructionSets);
    const newSet = { id, instructions: markdown };
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await setInstructions(id, markdown);
      setInstructionSets([...instructionSets, newSet]);
      setSelectedSet(newSet);
      setShowConfirmModal(false);

      try {
        const propagation = await propagateInstructionVersionToAssistants(oldId, id);
        const parts = [`Saved ${id}.`];
        if (propagation.assistantsUpdated > 0) {
          parts.push(`Updated ${propagation.assistantsUpdated} assistant(s).`);
        }
        if (propagation.talkliveInstancesUpdated > 0) {
          parts.push(`Updated ${propagation.talkliveInstancesUpdated} TalkLive instance(s).`);
        }
        if (propagation.affected === 0) {
          parts.push('No assistants were using the previous version.');
        }
        if (propagation.assistantsFailed > 0) {
          parts.push(
            `Failed to update ${propagation.assistantsFailed} assistant(s): ${propagation.assistantsFailedList.join(', ')}.`
          );
        }
        if (propagation.talkliveInstancesFailed > 0) {
          parts.push(
            `Failed to update ${propagation.talkliveInstancesFailed} TalkLive instance(s): ${propagation.talkliveFailedDetails.join(', ')}.`
          );
        }
        setSuccessMessage(parts.join(' '));
      } catch (propagationErr) {
        console.error('Failed to propagate instruction version to assistants:', propagationErr);
        setSuccessMessage(`Saved ${id}, but failed to update assistants using ${oldId}.`);
      }
    } catch (err) {
      setError('Failed to save new version');
    }
    setLoading(false);
  };

  // Delete logic
  const handleDelete = () => {
    setShowDeleteModal(true);
  };
  const handleConfirmDelete = async () => {
    if (!selectedSet) return;
    setLoading(true);
    setError(null);
    try {
      await deleteInstructions(selectedSet.id);
      setInstructionSets(instructionSets.filter(s => s.id !== selectedSet.id));
      setSelectedSet(null);
      setMarkdown('');
      setShowDeleteModal(false);
    } catch (err) {
      setError('Failed to delete instructions');
    }
    setLoading(false);
  };

  // Open Apply To modal and fetch assistants
  const handleOpenApplyModal = async () => {
    setShowApplyModal(true);
    setApplyToFields({ ...DEFAULT_APPLY_TO_FIELDS });
    setSelectedAssistants([]);
    setApplyLoading(true);
    try {
      const res = await getAllDealerAssistant();
      setAllAssistants(res.data.data || []);
    } catch (err) {
      setError('Failed to fetch assistants');
    }
    setApplyLoading(false);
  };

  const handleApplyFieldToggle = (field) => {
    setApplyToFields((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const selectedApplyFields = ASSISTANT_INSTRUCTION_ID_FIELDS.filter((field) => applyToFields[field]);

  // Apply instructions to selected assistants
  const handleApplyInstructions = async () => {
    if (selectedApplyFields.length === 0) {
      setError('Select at least one instruction field to apply.');
      return;
    }
    if (selectedAssistants.length === 0) return;

    setApplyLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const instructionId = selectedSet?.id;
      const assistantIds = selectedAssistants.map((a) => a.value);
      const onlyBasicInstructionsId =
        selectedApplyFields.length === 1 && selectedApplyFields[0] === 'instructions_id';

      setShowApplyModal(false);

      if (onlyBasicInstructionsId) {
        await applyInstructions(assistantIds, instructionId);
        setSuccessMessage(
          `Applied ${instructionId} to ${assistantIds.length} assistant(s): ${APPLY_FIELD_LABELS.instructions_id}.`
        );
      } else {
        const selectedIds = new Set(assistantIds);
        const assistantsToUpdate = allAssistants.filter((a) => selectedIds.has(a.assistant_id));

        const results = await Promise.allSettled(
          assistantsToUpdate.map((assistant) =>
            setDealerAssistant(applyInstructionFieldsToAssistant(assistant, instructionId, selectedApplyFields))
          )
        );

        const updated = results.filter((r) => r.status === 'fulfilled').length;
        const failedAssistants = results
          .map((result, index) => ({ result, assistant: assistantsToUpdate[index] }))
          .filter(({ result }) => result.status === 'rejected')
          .map(({ assistant }) => formatAssistantLabel(assistant));
        const fieldLabels = selectedApplyFields.map((f) => APPLY_FIELD_LABELS[f]).join(', ');

        if (failedAssistants.length > 0) {
          setSuccessMessage(
            `Applied ${instructionId} to ${updated} assistant(s) (${fieldLabels}). Failed for ${failedAssistants.length} assistant(s): ${failedAssistants.join(', ')}.`
          );
        } else {
          setSuccessMessage(
            `Applied ${instructionId} to ${updated} assistant(s): ${fieldLabels}.`
          );
        }
      }
    } catch (err) {
      setError('Failed to apply instructions');
    }
    setApplyLoading(false);
  };

  // Options for select
  const options = instructionSets.map(s => ({ value: s.id, label: s.id }));

  return (
    <div className="capture-creation">
      <h2>Instructions Manager</h2>
      {loading && <div className="loading" style={{ color: 'blue', marginBottom: 10 }}>Loading...</div>}
      {error && <div className="error" style={{ color: 'red', marginBottom: 10 }}>{error}</div>}
      {successMessage && <div style={{ color: 'green', marginBottom: 10 }}>{successMessage}</div>}
      <div className="form-group">
        <label>Choose Instruction Set</label>
        <Select options={options} onChange={handleSelect} value={selectedSet ? { value: selectedSet.id, label: selectedSet.id } : null} isClearable isDisabled={loading} />
      </div>
      <div className="form-group">
        <button onClick={handleCreate} disabled={loading}>Create New</button>
        <button onClick={handleSave} disabled={!markdown || loading}>Save</button>
        <button onClick={handleDelete} disabled={!selectedSet || loading}>Delete</button>
        <button onClick={handleOpenApplyModal} disabled={!selectedSet || loading}>Apply To</button>
      </div>
      <div className="form-group">
        <label>Edit Instructions (Markdown)</label>
        <textarea value={markdown} onChange={e => { setError(null); setMarkdown(e.target.value); }} rows={10} disabled={loading} />
      </div>
      <div className="form-group">
        <label>Preview</label>
        <div style={{ border: '1px solid #ccc', borderRadius: 4, padding: 10, minHeight: 100, background: '#fff' }}>
          <ReactMarkdown>{markdown}</ReactMarkdown>
        </div>
      </div>
      {/* Modals */}
      <Modal isOpen={showNameModal} title="Name New Instruction Set" onClose={() => setShowNameModal(false)}>
        <input value={newSetName} onChange={e => setNewSetName(e.target.value)} placeholder="Set Name" />
        <button onClick={handleNameSubmit} disabled={!newSetName}>Save</button>
      </Modal>
      <Modal isOpen={showConfirmModal} title="Save New Version?" onClose={() => setShowConfirmModal(false)}>
        <p>This will create a new version of the current set and update all assistants using the previous version. Continue?</p>
        <button onClick={handleConfirmSave}>Yes</button>
      </Modal>
      <Modal isOpen={showDeleteModal} title="Delete Instruction Set?" onClose={() => setShowDeleteModal(false)}>
        <p>Are you sure you want to delete this set?</p>
        <button onClick={handleConfirmDelete}>Delete</button>
      </Modal>
      <Modal
        isOpen={showApplyModal}
        title="Apply Instructions To Assistants"
        onClose={() => setShowApplyModal(false)}
        contentClassName="instructions-apply-modal"
        contentStyle={{ minHeight: '420px', maxWidth: '520px', display: 'flex', flexDirection: 'column' }}
      >
        {applyLoading ? (
          <div>Loading assistants...</div>
        ) : (
          <>
            <p style={{ marginTop: 0, marginBottom: 12, fontSize: 14, color: '#555' }}>
              Applying: <strong>{selectedSet?.id}</strong>
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Instruction fields</label>
              {ASSISTANT_INSTRUCTION_ID_FIELDS.map((field) => (
                <label
                  key={field}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, cursor: 'pointer', fontSize: 14 }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(applyToFields[field])}
                    onChange={() => handleApplyFieldToggle(field)}
                    style={{ width: 16, height: 16 }}
                  />
                  {APPLY_FIELD_LABELS[field]}
                </label>
              ))}
            </div>
            <div style={{ marginBottom: 16, flex: 1, minHeight: 180 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Assistants</label>
              <Select
                isMulti
                options={allAssistants.map((a) => ({ value: a.assistant_id, label: a.name || a.assistant_id }))}
                value={selectedAssistants}
                onChange={setSelectedAssistants}
                placeholder="Select assistants"
                isDisabled={applyLoading}
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <button
              onClick={handleApplyInstructions}
              disabled={applyLoading || selectedAssistants.length === 0 || selectedApplyFields.length === 0}
            >
              Apply
            </button>
          </>
        )}
      </Modal>
    </div>
  );
};

export default Instructions;
